import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import './ChangePassword.css';
import { useNavigate } from "react-router-dom";
import useOtpTimer from "../../../hooks/useOtpTimer";
import { changePasswordOtp, changePasswordSubmit, verifyOtpChangePass } from "../../../services/changePasswordService";
import { validatePassword,capitalizeFirstLetter } from "../../../utils/validations";

const ChangePassword = () => {
    const { timer, startTimer, stopTimer } = useOtpTimer(60);
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [otpStatus, setOtpStatus] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });

    // Move email, usernameEmployee, and mobile to the top to ensure they're available in handleSubmit
    const usernameEmployee = localStorage.getItem('full_name');
    const mobile = localStorage.getItem('mobile');
    const email = localStorage.getItem('email');

    const handleSendOtp = async () => {
        try {
            await changePasswordOtp(email);
            setOtpStatus('OTP sent successfully!');
            startTimer();
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send OTP');
            setOtpStatus('');
        }
    };

    const handleVerifyOtp = async () => {
        try {
            await verifyOtpChangePass(email, otp);
            setOtpStatus('verified');
            setError('');
            stopTimer();
            return true;
        } catch (err) {
            setOtpStatus('❌ Invalid OTP');
            setError(err.response?.data?.message || 'OTP verification failed');
            return false;
        }
    };

    const getPasswordStrengthMessage = () => {
        if (!newPassword) return "";
        const isStrong = validatePassword(newPassword);
        return isStrong
            ? { message: "Strong password", className: "strong" }
            : {
                message: "Please make your password atleast 8 characters long and use mix of UPPER and lowercase letters, numbers and symbols",
                className: "weak",
            };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log("Submit clicked!");

        if (!validatePassword(newPassword)) {
            setError("New password does not meet strength requirements!");
            return;
        }

        if (newPassword !== confirmPassword) {
            setError("New password and confirm password do not match!");
            return;
        }

        // Use email from localStorage
        const payload = {
            userName: email, // Use email here
            currentPassword,
            newPassword,
        };

        console.log("Submitting payload:", payload);

        try {
            await changePasswordSubmit(payload);
            setSuccess('Password changed successfully!');
            setError(''); // Clear any previous errors
            formClear();
        } catch (error) {
            console.error("Change password error:", error);
            const backendError =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                "An unknown error occurred while changing the password.";

            setError(backendError);
            setSuccess('');


        }
    };

    const formClear = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setOtp('');
        setError('');
        setSuccess('');
        setOtpStatus('');
    };

    const gotoHome = () => {
        navigate("/dashboard");
    };

    return (
        <div className="changepassword-container">
            <div className="changepassword-box">
                <h2 className="changepassword-heading">Change Password</h2>

                <div className="changepassword-input-container-floating-label">
                    <input
                        type="text"
                        value={capitalizeFirstLetter(usernameEmployee)}
                        readOnly
                        placeholder=" "
                    />
                    <label><span className="required">*</span> Username</label>
                </div>

                <div className="changepassword-input-container-floating-label">
                    <input
                        type="text"
                        value={mobile}
                        readOnly
                        placeholder=" "
                    />
                    <label><span className="required">*</span> Mobile Number</label>
                </div>

                <div className="changepassword-input-container-floating-label password-field">
                    <input
                        type={showPassword.current ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        placeholder=" "
                    />
                    <label><span className="required">*</span> Current Password</label>
                    <span
                        className="eye-icon"
                        onClick={() => setShowPassword({ ...showPassword, current: !showPassword.current })}
                    >
                        {showPassword.current ? <EyeOff size={18} /> : <Eye size={18} />}
                    </span>
                </div>

                <div className="changepassword-input-container-floating-label password-field">
                    <input
                        type={showPassword.new ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        placeholder=" "
                    />
                    <label><span className="required">*</span> New Password</label>
                    <span
                        className="eye-icon"
                        onClick={() => setShowPassword({ ...showPassword, new: !showPassword.new })}
                    >
                        {showPassword.new ? <EyeOff size={18} /> : <Eye size={18} />}
                    </span>
                </div>
                {currentPassword && newPassword && (
                    <p className={`match-message ${newPassword === currentPassword ? 'no-match' : 'match'}`}>
                        {newPassword === currentPassword ? 'Current password and new password must be different' : ' '}
                    </p>
                )}
                <p className={`password-strength ${getPasswordStrengthMessage().className}`}>
                    {getPasswordStrengthMessage().message}
                </p>

                <div className="changepassword-input-container-floating-label password-field">
                    <input
                        type={showPassword.confirm ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        placeholder=" "
                    />
                    <label><span className="required">*</span> Confirm New Password</label>
                    <span
                        className="eye-icon"
                        onClick={() => setShowPassword({ ...showPassword, confirm: !showPassword.confirm })}
                    >
                        {showPassword.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </span>
                </div>
                {confirmPassword && (
                    <p className={`match-message ${newPassword === confirmPassword ? 'match' : 'no-match'}`}>
                        {newPassword === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                    </p>
                )}


                <div className="changepassword-input-container-floating-label otp-field">
                    <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        placeholder=" "
                        disabled={timer === 0 && !otpStatus.includes('sent')}
                    />
                    <label><span className="required">*</span> Enter OTP</label>
                    {otpStatus !== 'verified' && (
                        <button
                            type="button"
                            className="otp-btn"
                            onClick={handleSendOtp}
                            disabled={timer > 0}
                        >
                            {timer > 0 ? `Resend OTP in ${timer}s` : 'Send OTP'}
                        </button>
                    )}

                </div>

                {otpStatus === 'verified' && (
                    <p className="otp-status match">✅ Verified</p>
                )}

                {otp && otpStatus !== 'verified' && (
                    <button
                        type="button"
                        className="verify-otp-btn"
                        onClick={handleVerifyOtp}
                    >
                        Verify OTP
                    </button>
                )}

                {/* Ensure error message is visible */}
                {error && <p className="error-message-pass">{error}</p>}
                {success && <p className="success-message">{success}</p>}
                <div className="button-group">
                    <button
                        type="button"
                        className="ss-btn"
                        onClick={(e) => handleSubmit(e)}
                        disabled={
                            !currentPassword.trim() ||
                            !newPassword.trim() ||
                            !confirmPassword.trim() ||
                            currentPassword === newPassword ||
                            newPassword !== confirmPassword ||
                            otpStatus !== 'verified' // Ensure OTP is verified before allowing submission
                        }
                    >
                        Submit
                    </button>

                    <button type="button" className="clear-btn" onClick={formClear}>
                        Clear
                    </button>
                    <button type="button" className="cancel-btn" onClick={gotoHome}>Cancel</button>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;